/**
 * useTemplateComputed
 * Extracted computed values; used by NC/MAS Direct/MAS Indirect template core hooks.
 * Contains useMemo / useCallback hooks for derived UI data.
 */
import { useCallback, useMemo } from 'react';
import { SECTION_DISPLAY_NAMES, SECTION_NAMES } from '../constants/sectionNames';
import { calculateProgress, parseDateString } from '../bidAnalysisTemplate.logic';
import { calculate5YearSummary as calculate5YearSummaryUtil } from '../shared/utils/calculate5YearSummary';
import type { NavigationItem } from '../shared/types';

export interface UseTemplateComputedDeps {
  bidDetails: any;
  formData: any;
  selectedConcept: string | null;
  packSizeFormData: Record<number, any>;
  activePackSizeTab: number | null;
  ndcValidated: boolean;
  marketSalesTrendData: any[];
  contractPriceRanking: any[];
  checkSectionCompletionWrapper: (sectionId: string) => 'completed' | 'warning';
  copyAllYearsDataRef: React.MutableRefObject<any>;
  yearFormDataRef: React.MutableRefObject<Record<number, Record<number, any>>>;
  yearFormData: Record<number, Record<number, any>>;
  supplierName: string;
}

export function useTemplateComputed(deps: UseTemplateComputedDeps) {
  const {
    bidDetails,
    formData,
    selectedConcept,
    packSizeFormData,
    activePackSizeTab,
    ndcValidated,
    marketSalesTrendData,
    contractPriceRanking,
    checkSectionCompletionWrapper,
    copyAllYearsDataRef,
    yearFormDataRef,
    yearFormData,
    supplierName,
  } = deps;

  const productName = bidDetails?.product_name || 'Bid Analysis';
  const productType: 'direct' | 'indirect' | null = bidDetails?.product_type ?? null;
  const templateType: string = bidDetails?.template_type || 'mas';
  const isIndirect = productType === 'indirect';

  const navigationItems: NavigationItem[] = useMemo(() => {
    const currentIsNC = templateType === 'nc';

    const items: NavigationItem[] = [
      {
        id: 'project-details',
        label: SECTION_DISPLAY_NAMES[SECTION_NAMES.PROJECT_INFORMATION],
        icon: 'pi pi-info-circle',
        status: checkSectionCompletionWrapper('project-details'),
      },
      {
        id: 'product-info',
        label: SECTION_DISPLAY_NAMES[SECTION_NAMES.PRODUCT_DETAILS],
        icon: 'pi pi-box',
        status: checkSectionCompletionWrapper('product-info'),
      },
    ];

    items.push(
      {
        id: 'current-market-state',
        label: SECTION_DISPLAY_NAMES[SECTION_NAMES.CURRENT_MARKET_STATE],
        icon: 'pi pi-chart-bar',
        status: checkSectionCompletionWrapper('current-market-state'),
      },
      {
        id: 'cost-analysis',
        label: SECTION_DISPLAY_NAMES[SECTION_NAMES.COGS_BUILDUP],
        icon: 'pi pi-calculator',
        status: checkSectionCompletionWrapper('cost-analysis'),
      },
      {
        id: 'pricing-strategy',
        label: SECTION_DISPLAY_NAMES[SECTION_NAMES.PRICING_CALCULATOR],
        icon: 'pi pi-dollar',
        status: checkSectionCompletionWrapper('pricing-strategy'),
      },
      {
        id: 'competitive-analysis',
        label: SECTION_DISPLAY_NAMES[SECTION_NAMES.COMPETITIVE_ANALYSIS],
        icon: 'pi pi-chart-line',
        status: checkSectionCompletionWrapper('competitive-analysis'),
      },
      {
        id: 'contract-price-ranking',
        label: SECTION_DISPLAY_NAMES[SECTION_NAMES.CONTRACT_PRICE_RANKING],
        icon: 'pi pi-list',
        status: checkSectionCompletionWrapper('contract-price-ranking'),
      }
    );

    if (!currentIsNC) {
      items.push({
        id: 'tmop-calculator',
        label: SECTION_DISPLAY_NAMES[SECTION_NAMES.TMOP_CALCULATOR],
        icon: 'pi pi-percentage',
        status: checkSectionCompletionWrapper('tmop-calculator'),
      });
    }

    return items;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    ndcValidated,
    formData,
    packSizeFormData,
    activePackSizeTab,
    marketSalesTrendData,
    contractPriceRanking,
    templateType,
    checkSectionCompletionWrapper,
  ]);

  const concepts = useMemo(() => {
    if (!bidDetails?.form_details?.form_data) return [];
    return bidDetails.form_details.form_data.map((concept: any) => ({
      label: concept.concept_name || '',
      value: concept.concept_name || '',
    }));
  }, [bidDetails]);

  const packSizeTabs = useMemo(() => {
    if (!bidDetails?.form_details?.form_data) return [];

    const tabs: Array<{ index: number; packSize: string; conceptName: string }> = [];
    bidDetails.form_details.form_data.forEach((concept: any, conceptIndex: number) => {
      if (selectedConcept && concept.concept_name !== selectedConcept) {
        return;
      }
      const packSizes = Object.keys(concept.pack_size || {});
      packSizes.forEach((packSize, packIndex) => {
        const tabIndex = conceptIndex * 1000 + packIndex;
        tabs.push({
          index: tabIndex,
          packSize: packSize,
          conceptName: concept.concept_name || '',
        });
      });
    });

    return tabs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bidDetails, selectedConcept, productName]);

  const allPackSizeTabs = useMemo(() => {
    if (!bidDetails?.form_details?.form_data) return [];

    const tabs: Array<{ index: number; packSize: string; conceptName: string }> = [];
    bidDetails.form_details.form_data.forEach((concept: any, conceptIndex: number) => {
      const packSizes = Object.keys(concept.pack_size || {});
      packSizes.forEach((packSize, packIndex) => {
        const tabIndex = conceptIndex * 1000 + packIndex;
        tabs.push({
          index: tabIndex,
          packSize: packSize,
          conceptName: concept.concept_name || '',
        });
      });
    });

    return tabs;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bidDetails, productName]);

  const yearTabs = useMemo(() => {
    if (templateType !== 'nc') return [];

    const tabs: Array<{ index: number; label: string; year: number | null }> = [];
    const targetLaunchDate = formData.targetLaunchDate;
    let baseYear: number | null = null;

    if (targetLaunchDate) {
      const date =
        typeof targetLaunchDate === 'string' ? new Date(targetLaunchDate) : targetLaunchDate;
      if (!isNaN(date.getTime())) {
        baseYear = date.getFullYear();
      }
    }

    tabs.push({ index: 0, label: 'Base Year', year: baseYear });
    for (let i = 1; i <= 4; i++) {
      tabs.push({ index: i, label: `Option Year ${i}`, year: baseYear ? baseYear + i : null });
    }
    tabs.push({ index: 5, label: '5 Year Summary', year: null });

    return tabs;
  }, [templateType, formData.targetLaunchDate]);

  const formatTargetLaunchDateForDisplay = useCallback(
    (targetLaunchDate: string | Date | null | undefined, yearOffset: number = 0): string => {
      if (!targetLaunchDate) return '';
      const baseDate =
        typeof targetLaunchDate === 'string'
          ? parseDateString(targetLaunchDate)
          : targetLaunchDate instanceof Date
            ? targetLaunchDate
            : new Date(targetLaunchDate);
      if (!baseDate || isNaN(baseDate.getTime())) return '';
      const yearDate = new Date(baseDate);
      yearDate.setFullYear(baseDate.getFullYear() + yearOffset);
      const month = String(yearDate.getMonth() + 1).padStart(2, '0');
      const day = String(yearDate.getDate()).padStart(2, '0');
      const year = yearDate.getFullYear();
      return `${month}/${day}/${year}`;
    },
    []
  );

  const calculate5YearSummary = () =>
    calculate5YearSummaryUtil({
      copyAllYearsData: copyAllYearsDataRef.current,
      yearFormDataRefCurrent: yearFormDataRef.current,
      yearFormData,
      templateType,
      allPackSizeTabs,
      activePackSizeTab,
      supplierName: supplierName || '',
      formData,
      packSizeFormData,
    });

  const calculateProgressWrapper = useCallback((): number => {
    if (!bidDetails) return 0;

    const currentTemplateType = bidDetails?.template_type || 'mas';
    const tabsForProgress = currentTemplateType === 'nc' ? allPackSizeTabs : packSizeTabs;

    return calculateProgress({
      yearFormData,
      templateType: currentTemplateType,
      bidDetails,
      packSizeTabs: tabsForProgress,
      packSizeFormData,
      formData,
      supplierName,
      marketSalesTrendData: [],
      contractPriceRanking: [],
    });
  }, [
    bidDetails,
    packSizeTabs,
    allPackSizeTabs,
    packSizeFormData,
    yearFormData,
    formData,
    supplierName,
  ]);

  return {
    productName,
    productType,
    templateType,
    isIndirect,
    navigationItems,
    concepts,
    packSizeTabs,
    allPackSizeTabs,
    yearTabs,
    formatTargetLaunchDateForDisplay,
    calculate5YearSummary,
    calculateProgressWrapper,
  };
}
